import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from "@/components/ui/button";
import { EmptyState } from "@supabank/ui";
import { Heading, Text } from "@/components/ui/typography";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { semanticTokens } from "@packages/config/theme";
import { CreditCard, Plus, RefreshCw } from "lucide-react";
import { AccountSelectionCard } from "@/features/user/wallet/AccountSelectionCard";
import { ExternalAccount } from "@/features/user/wallet/hooks/useWallet";

// Inline skeleton - shape-matching for bank account card
function BankAccountCardSkeleton() {
  return (
    <div className={cn("flex items-center gap-4 p-4 rounded-xl", "bg-card/50 border border-border/50")}>
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
  );
}

interface BankAccountsCardProps {
  accounts: ExternalAccount[];
  loading: boolean;
  onAddAccount?: () => void;
  onRefresh?: () => void;
  onAccountClick?: (account: ExternalAccount) => void;
  isRefreshing?: boolean;
}

/**
 * BankAccountsCard — Hero Liquid Glass карточка для банковских счетов
 * 
 * Паттерн: BalanceCard (WalletPage)
 * - Liquid Glass стиль (rounded-[28px], backdrop-blur)
 * - Кнопки действий внутри CardHeader
 * - Touch targets ≥44px
 * - Motion: 240ms canonical
 */
export function BankAccountsCard({ 
  accounts, 
  loading, 
  onAddAccount,
  onRefresh,
  onAccountClick,
  isRefreshing = false
}: BankAccountsCardProps) {
  // Filter active accounts
  const activeAccounts = accounts.filter(acc => acc && typeof acc === 'object' && acc.status === 'active');

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        // Liquid Glass Surface tier (Hero card: rounded-[28px])
        "rounded-[28px]",
        "bg-gradient-to-br from-white/90 via-white/70 to-white/90",
        "dark:from-slate-900/80 dark:via-slate-800/65 dark:to-slate-900/80",
        "backdrop-blur-xl backdrop-saturate-[180%]",
        "dark:backdrop-saturate-150 dark:brightness-90",
        "border border-white/20 dark:border-white/10",
        "shadow-[0_8px_32px_hsl(220_15%_5%/0.12),inset_0_1px_0_hsl(0_0%_100%/0.8)]",
        "dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.5),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
        semanticTokens.motion.standard
      )}
    >
      {/* Specular highlight (top edge reflection) — Liquid Glass signature */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20" />
      
      {/* Content container */}
      <div className="relative z-10">
        <Card className="border-0 bg-transparent shadow-none">
          <CardHeader>
            <div className={cn("flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4")}>
              <CardTitle asChild>
                <Heading level={3} className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" aria-hidden="true" />
                  Bank Accounts
                  {activeAccounts.length > 0 && (
                    <Text as="span" variant="bodySmall" color="muted" className="ml-1">
                      ({activeAccounts.length})
                    </Text>
                  )}
                </Heading>
              </CardTitle>
              
              {/* Quick Actions — Revolut-style: в header карточки */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* Refresh Button */}
                {onRefresh && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onRefresh}
                    disabled={isRefreshing || loading}
                    className={cn(
                      "min-h-[44px] min-w-[44px]",
                      semanticTokens.motion.standard
                    )}
                    aria-label={isRefreshing ? "Refreshing..." : "Refresh accounts"}
                  >
                    <RefreshCw 
                      className={cn("h-5 w-5", isRefreshing && "animate-spin")} 
                      aria-hidden="true" 
                    />
                  </Button>
                )}
                
                {/* Add Account Button */}
                {onAddAccount && (
                  <Button
                    variant="default"
                    size="sm"
                    className={cn("min-h-[44px] min-w-[100px] sm:min-w-[120px]")}
                    onClick={onAddAccount}
                    aria-label="Add bank account"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                    <span className="hidden sm:inline">Add Account</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}
              </div>
            </div>
            
            {/* Subtitle */}
            <Text variant="bodySmall" color="muted" className="mt-2">
              Withdraw funds to your connected accounts
            </Text>
          </CardHeader>
          
          <CardContent className={cn("space-y-6", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
            {loading ? (
              <div 
                className={cn("space-y-3", semanticTokens.spacing.sm.replace('p-', 'space-y-'))} 
                role="status" 
                aria-live="polite" 
                aria-label="Loading bank accounts"
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <BankAccountCardSkeleton key={i} />
                ))}
              </div>
            ) : activeAccounts.length === 0 ? (
              <EmptyState
                icon={CreditCard}
                title="No bank accounts connected"
                description="Add an account to withdraw funds to your bank."
                action={{
                  label: "Add Bank Account",
                  onClick: onAddAccount
                }}
              />
            ) : (
              <div 
                className={cn("grid gap-3", semanticTokens.spacing.sm.replace('p-', 'gap-'))} 
                role="list" 
                aria-label="Your bank accounts"
              >
                {activeAccounts.map(account => {
                  if (!account || typeof account !== 'object' || !account.id) {
                    return null;
                  }
                  return (
                    <AccountSelectionCard
                      key={account.id}
                      account={account}
                      isSelected={false}
                      onClick={() => onAccountClick?.(account)}
                    />
                  );
                }).filter(Boolean)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

